import type { PayoutView } from "@/services/types";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";

/**
 * The calculation statement — the most scrutinised element on the screen. A
 * clean vertical breakdown from prize to net payable, with every deduction shown
 * as a negative in red and the net payable given clear weight at the bottom.
 * Other-group balances are listed in a separate, clearly-labelled block that is
 * never deducted.
 */
export function CalculationStatement({ payout }: { payout: PayoutView }) {
  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <div className="border-b border-background-100 px-4 py-3">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground-900">
          <i className="ri-calculator-line text-primary-600" />
          Payout calculation
        </h3>
        <p className="mt-0.5 text-xs text-foreground-500">Prize less same-group dues and deductions</p>
      </div>

      <dl className="space-y-2 px-4 py-3 text-sm">
        <div className="flex items-center justify-between">
          <dt className="text-foreground-800">
            {payout.isOrganizer || payout.isOrganizerReserved
              ? `Organizer Payout (${payout.isOrganizerReserved ? "Reserved Cycle: Pool + Commission" : "Auction Won by Organizer"})`
              : "Winning Bid Prize (Non-Organizer Winner)"}
          </dt>
          <dd className="tnum text-base font-semibold text-foreground-950"><CurrencyDisplay amount={payout.prize} hideSymbol /></dd>
        </div>

        {payout.sameGroupDues.length > 0 ? (
          <div className="border-t border-background-100 pt-2">
            <p className="mb-1.5 text-xs font-medium text-foreground-500">Same-group dues adjusted (recovered from the prize)</p>
            {payout.sameGroupDues.map((d) => (
              <div key={d.cycle} className="flex items-center justify-between py-0.5">
                <dt className="text-foreground-600">Cycle {d.cycle} · {d.periodLabel}</dt>
                <dd className="tnum font-medium text-danger-600">− <CurrencyDisplay amount={d.amount} hideSymbol /></dd>
              </div>
            ))}
          </div>
        ) : (
          <div className="border-t border-background-100 pt-2 flex items-center justify-between">
            <dt className="text-foreground-600">Same-group dues</dt>
            <dd className="text-success-700">None — nothing adjusted</dd>
          </div>
        )}

        {payout.commissionAmount && payout.commissionAmount > 0 ? (
          <div className="border-t border-background-100 pt-2 text-xs">
            <div className="flex items-center justify-between text-foreground-700">
              <span className="flex items-center gap-1">
                <i className="ri-shield-user-line text-primary-600" />
                <span>Cycle Commission</span>
              </span>
              <span className="tnum font-semibold text-primary-700">
                <CurrencyDisplay amount={payout.commissionAmount} hideSymbol />
              </span>
            </div>
            <p className="mt-0.5 text-[11px] text-foreground-500">
              {payout.isOrganizer || payout.isOrganizerReserved
                ? "Included in the organizer total payout above."
                : "Credited to Organizer Account. The winning non-organizer member receives the winning amount only."}
            </p>
          </div>
        ) : null}

        <div className="flex items-center justify-between rounded-md bg-background-100 px-3 py-2.5">
          <dt className="text-sm font-semibold text-foreground-900">Net payable</dt>
          <dd className="tnum text-xl font-bold text-primary-700"><CurrencyDisplay amount={payout.netPayable} hideSymbol /></dd>
        </div>
      </dl>

      <div className="border-t border-background-100 px-4 py-3">
        <div className="flex items-center justify-between text-sm">
          <dt className="text-foreground-600">Amount already paid</dt>
          <dd className="tnum font-medium text-foreground-900"><CurrencyDisplay amount={payout.amountPaid} hideSymbol /></dd>
        </div>
        <div className="mt-1 flex items-center justify-between text-sm">
          <dt className="text-foreground-600">Balance still owed</dt>
          <dd className={`tnum font-semibold ${payout.balanceOwed > 0 ? "text-warning-700" : "text-success-700"}`}>
            <CurrencyDisplay amount={payout.balanceOwed} hideSymbol />
          </dd>
        </div>
      </div>

      {payout.otherGroupBalances.length > 0 ? (
        <div className="border-t border-background-100 px-4 py-3">
          <div className="flex items-center gap-1.5">
            <i className="ri-information-line text-info-600" />
            <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500">Balances in other groups — not deducted</p>
          </div>
          <p className="mt-1 text-xs text-foreground-500">
            This member owes the following in other groups. These are shown for awareness only and are <strong>never</strong> adjusted
            against this group's payout — that money belongs to the other circles.
          </p>
          <div className="mt-2 space-y-1">
            {payout.otherGroupBalances.map((b) => (
              <div key={b.groupId} className="flex items-center justify-between rounded-md bg-secondary-100/60 px-3 py-1.5 text-sm">
                <span className="text-secondary-900">{b.groupName} <span className="tnum text-xs text-secondary-700">· {b.groupCode}</span></span>
                <span className="tnum font-medium text-secondary-900"><CurrencyDisplay amount={b.outstanding} hideSymbol /></span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}